import { ElectionType } from "./election-type";
import { State } from "./state";

export enum ChartTypeEnum {
  GENERAL_ELECTION,
  PARTISAN_PRIMARY,
  NON_PARTISAN,
  SPECIAL_ELECTION,
  PRESIDENTIAL_PRIMARY,
}

export enum ChartStatusEnum {
  InProgress = "In Progress",
  Completed = "Completed",
  Errored = "Errored",
}

export enum ChartStatusColorEnum {
  Completed = "text-green-700 bg-green-100",
  InProgress = "text-yellow-700 bg-yellow-100",
  Errored = "text-red-700 bg-red-100",
  Printed = "text-gray-700 bg-gray-200",
}

export enum ChartOrderByEnum {
  false = "Oldest To Newest",
  true = "Newest To Oldest",
  modifiedAt = "Last Updated",
}

export type ChartOrderBy = keyof typeof ChartOrderByEnum;
export type ChartStatus = keyof typeof ChartStatusEnum;

export type ChartListResponse = {
  id: string;
  adminChart: boolean;
  county: string;
  createdAt: string;
  downloadLink: string;
  electionDate: string;
  electionType: ElectionType;
  showCandidateNames: boolean;
  createStraightParty?: boolean;
  labelsOnRight: boolean;
  modifiedAt: string;
  modifiedBy: string;
  municipality: string;
  precinct: string;
  printRequested: boolean;
  printed: boolean;
  printedBy: string;
  printedDate: string;
  fullChartDownloadLink: string;
  hasBallotFile: boolean;
  state: State;
  status: ChartStatus;
  userId: string;
  deleteChart: (id: string) => Promise<void>;
  politicalParty: ChartRacePoliticalPartiesResponse
};

export type CreateChartPoliticalPartyRequest = {
  abbv: string;
  name: string;
  order: number;
};

export type CreateChartRaceBallotMarksRequest = {
  isOnSecondPage: boolean;
  order: number;
  x: number;
  y: number;
};

export type CreateChartRacePoliticalPartyRequest = {
  abbv: string;
  candidates: number;
  name: string;
  order: number;
  candidateNames: CreateCandidateNameRequest[] | []
};

export type CreateChartRaceRequest = {
  candidates?: number;
  marks: CreateChartRaceBallotMarksRequest[];
  name: string;
  order: number;
  candidateNames: CreateCandidateNameRequest[] | [];
  politicalParties: CreateChartRacePoliticalPartyRequest[];
  raceTypeId: number;
  type: string;
  voteFor: number;
  writeIns: number;
};

export type CreateChartRequest = {
  adminchart: boolean;
  county: string;
  electionTypeId: number;
  electiondate: string;
  showCandidateNames: boolean;
  labelsonright: boolean;
  municipality: string;
  politicalParties: CreateChartPoliticalPartyRequest[];
  precinct: string;
  races: CreateChartRaceRequest[];
  stateId: number;
  userId?: string;
  presidentialBallotFiles?: CreateChartPresidentialBallotFileRequest[];
  createStraightParty?: boolean;
};

export type ChartBallotFileResponse = {
  id: string;
  downloadLink: string;
  firstPage: number;
  useSquareMarks: boolean;
  height: number;
  isTwoPages: boolean;
  name: string;
  secondPage: number;
  specificPages: boolean;
  width: number;
};

export type ChartPoliticalPartiesResponse = {
  id: number;
  abbv: string;
  name: string;
  order: number;
  candidateNames: CandidateNameResponse[] | [];
};

export type ChartRaceBallotMarksResponse = {
  id: string;
  isOnSecondPage: boolean;
  order: number;
  x: number;
  y: number;
};

export type ChartRacePoliticalPartiesResponse = {
  id: string;
  abbv: string;
  candidates: number;
  name: string;
  order: number;
  candidateNames: CandidateNameResponse[] | []
};

export type ChartRaceTypeResponse = { id: number; name: string };

export type ChartRaceCandidateNameResponse = {
  id?: string;
  name: string;
  order: number;
}

export type ChartRaceResponse = {
  id: string;
  ballotMarks: ChartRaceBallotMarksResponse[];
  candidates: number;
  name: string;
  order: number;
  candidateNames: ChartRaceCandidateNameResponse[] | [];
  politicalParties: ChartRacePoliticalPartiesResponse[];
  raceType: ChartRaceTypeResponse;
  type: string;
  voteFor: number;
  writeIns: number;
  isStraightParty: boolean;
};

export type ChartResponse = ChartListResponse & {
  ballotFile: ChartBallotFileResponse;
  politicalParties: ChartPoliticalPartiesResponse[];
  candidateNames: CandidateNameResponse[] | []
  races: ChartRaceResponse[];
  presidentialBallotFiles?: ChartPresidentialBallotFileResponse[];
};

export type PagedChartList = {
  charts: ChartListResponse[];
  page: number;
  pageSize: number;
  query: string;
  total: number;
};

export type UpdateChartPoliticalPartyRequest = {
  id: number;
  abbv: string;
  name: string;
  order: number;
  candidateNames: UpdateCandidateNameRequest[] | [];
};

export type UpdateChartRaceBallotMarksRequest = {
  id: string;
  isOnSecondPage: boolean;
  order: number;
  x: number;
  y: number;
};

export type UpdateChartRacePoliticalPartyRequest = {
  id: string;
  abbv: string;
  candidates: number;
  name: string;
  order: number;
  candidateNames: UpdateCandidateNameRequest[] | [];
};

export type UpdateCandidateNameRequest = {
  id?: string;
  name: string;
  order: number;
}

export type UpdateChartRaceRequest = {
  candidates?: number;
  id: string;
  ballotMarks: UpdateChartRaceBallotMarksRequest[];
  name: string;
  order: number;
  candidateNames: UpdateCandidateNameRequest[] | [];
  politicalParties: UpdateChartRacePoliticalPartyRequest[];
  raceTypeId: number;
  type: string;
  voteFor: number;
  writeIns: number;
};

export type ChartUpdateRequest = {
  id: string;
  adminchart: boolean;
  county: string;
  electionTypeId: number;
  electiondate: string;
  showCandidateNames: boolean;
  labelsonright: boolean;
  municipality: string;
  politicalParties: UpdateChartPoliticalPartyRequest[];
  precinct: string;
  races: UpdateChartRaceRequest[];
  stateId: number;
  status?: string;
  presidentialBallotFiles?: UpdateChartPresidentialBallotFileRequest[];
  createStraightParty?: boolean;
};

export type ChartPresidentialBallotFileResponse = {
  id: string;
  name: string;
  downloadLink: string;
  width: number;
  height: number;
  specificPages: boolean;
  useSquareMarks: boolean;
  isTwoPages: boolean;
  firstPage: number;
  secondPage: number;
  isNonPartisan: boolean;
  ballotMarks: ChartRaceBallotMarksResponse[];
  politicalParties: ChartRacePoliticalPartiesResponse[];
};

export type CreateChartPresidentialBallotFileRequest = {
  id: string;
  name: string;
  downloadLink: string;
  width: number;
  height: number;
  specificPages: boolean;
  useSquareMarks: boolean;
  isTwoPages: boolean;
  firstPage: number;
  secondPage: number;
  isNonPartisan: boolean;
  ballotMarks: ChartRaceBallotMarksResponse[];
  politicalParties: ChartRacePoliticalPartiesResponse[];
};

export type UpdateChartPresidentialBallotFileRequest = {
  id: string;
  name: string;
  downloadLink: string;
  width: number;
  height: number;
  specificPages: boolean;
  useSquareMarks: boolean;
  isTwoPages: boolean;
  firstPage: number;
  secondPage: number;
  isNonPartisan: boolean;
  ballotMarks: UpdateChartRaceBallotMarksRequest[];
  politicalParties: UpdateChartRacePoliticalPartyRequest[];
};

export type CreateCandidateNameRequest = {
  name: string;
  order: number;
}

export type CandidateNameResponse = {
  id: string;
  name: string;
  order: number;
}
